
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    createCalibrationSchedule,
    getCalibrationSchedules,
    updateCalibrationScheduleStatus,
    deleteCalibrationSchedule,
    downloadCalibrationSchedules,
    getLatestCalibrationDate,
    getJoinedGreenUsers
} from '../../Redux/actions';
import {
    Grid, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, CircularProgress,
    Pagination, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import AlertDialog from '../Common/AlertDialog';
import { Can } from '../../Context/Can';
import { paginationStyle, inputLabelFocusedStyle, selectFocusedStyle, buttonStyles } from '../../theme/formStyles';
import moment from 'moment';

export default function CalibrationSchedule({ facilityId, facilityName }) {
    const { client_id } = useParams();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    const [showLoader, setShowLoader] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [scheduleData, setScheduleData] = useState([]);
    const [latestCalibrationDate, setLatestCalibrationDate] = useState(null);
    const [greenUsers, setGreenUsers] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);
    const [msgType, setMsgType] = useState('success');
    const [saving, setSaving] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, scheduleId: null });

    // Pagination
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Dynamic schedule rows
    const [scheduleRows, setScheduleRows] = useState([
        { id: 1, schedule_date: null, assignee_name: '' }
    ]);

    useEffect(() => {
        if (facilityId && client_id) {
            if (!searchParams.get('page')) {
                setSearchParams({ page: 1 }, { replace: true });
                return;
            }
            const page = parseInt(searchParams.get('page')) || 1;
            fetchSchedules(page);
            fetchLatestCalibrationDate();
            fetchGreenUsers();
        }
    }, [facilityId, client_id, searchParams.get('page')]);

    const fetchSchedules = (page = 1) => {
        setShowLoader(true);
        dispatch(getCalibrationSchedules(client_id, facilityId, { page })).then(resp => {
            if (resp?.data?.success) {
                setScheduleData(resp.data.data || []);
                setTotalItems(resp.data.totalItems || 0);
                setTotalPages(resp.data.totalPages || 0);
                setCurrentPage(resp.data.currentPage || 1);
                setItemsPerPage(resp.data.itemsPerPage || 20);
            }
            setShowLoader(false);
        }).catch(() => setShowLoader(false));
    };

    const fetchLatestCalibrationDate = () => {
        dispatch(getLatestCalibrationDate(client_id, facilityId)).then(resp => {
            if (resp?.data?.success && resp.data.data?.latest_calibration_date) {
                setLatestCalibrationDate(resp.data.data.latest_calibration_date);
            }
        });
    };

    const fetchGreenUsers = () => {
        dispatch(getJoinedGreenUsers()).then(resp => {
            if (resp?.data?.success) {
                const activeUsers = resp.data.allData?.filter(item => item.status !== 'Inactive');
                const users = activeUsers?.map(item => item.name) || [];
                setGreenUsers(users);
            }
        });
    };

    const handleAddRow = () => {
        setScheduleRows(prev => [
            ...prev,
            { id: Date.now(), schedule_date: null, assignee_name: '' }
        ]);
    };

    const handleRemoveRow = (id) => {
        if (scheduleRows.length <= 1) return;
        setScheduleRows(prev => prev.filter(row => row.id !== id));
    };

    const handleRowChange = (id, field, value) => {
        setScheduleRows(prev => prev.map(row =>
            row.id === id ? { ...row, [field]: value } : row
        ));
    };

    const handleSave = () => {
        const validRows = scheduleRows.filter(row => row.schedule_date && row.assignee_name);
        if (validRows.length === 0) {
            setAlertMessage('Please fill at least one schedule with date and assignee');
            setMsgType('fail');
            return;
        }

        setSaving(true);
        const payload = {
            facility_id: facilityId,
            client_id,
            latest_calibration_date: latestCalibrationDate,
            schedules: validRows.map(row => ({
                schedule_date: dayjs(row.schedule_date).format('YYYY-MM-DD'),
                assignee_name: row.assignee_name
            }))
        };

        dispatch(createCalibrationSchedule(payload)).then(resp => {
            setSaving(false);
            if (resp?.data?.success) {
                setAlertMessage(resp.data.message);
                setMsgType('success');
                setShowAddForm(false);
                setScheduleRows([{ id: 1, schedule_date: null, assignee_name: '' }]);
                fetchSchedules();
            } else {
                setAlertMessage(resp?.data?.message || 'Failed to create schedule');
                setMsgType('fail');
            }
        }).catch(() => {
            setSaving(false);
            setAlertMessage('Something went wrong');
            setMsgType('fail');
        });
    };

    const handleDownload = () => {
        dispatch(downloadCalibrationSchedules(client_id, facilityId)).then(resp => {
            if (resp?.data?.success && resp.data.data) {
                const blob = new Blob([resp.data.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `calibration_schedule_${facilityName || facilityId}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                setAlertMessage('Failed to download');
                setMsgType('fail');
            }
        });
    };

    const handleStatusChange = (scheduleId, newStatus) => {
        if (newStatus === 'Completed') {
            setConfirmDialog({ open: true, scheduleId });
        }
    };

    const confirmStatusUpdate = () => {
        const { scheduleId } = confirmDialog;
        setConfirmDialog({ open: false, scheduleId: null });
        dispatch(updateCalibrationScheduleStatus(scheduleId, { client_id, status: 'Completed' })).then(resp => {
            if (resp?.data?.success) {
                setAlertMessage('Status updated to Completed');
                setMsgType('success');
                fetchSchedules(currentPage);
            } else {
                setAlertMessage(resp?.data?.message || 'Failed to update status');
                setMsgType('fail');
            }
        }).catch(() => {
            setAlertMessage('Something went wrong');
            setMsgType('fail');
        });
    };

    const handlePageChange = (event, newPage) => {
        setSearchParams({ page: newPage });
    };

    const scheduleColumnNames = ['Sl. No', 'Schedule Date', 'Assignee', 'Status'];

    let scheduleTable;
    if (showLoader) {
        scheduleTable = (
            <TableRow>
                <TableCell colSpan={scheduleColumnNames.length} style={{ textAlign: 'center', paddingTop: '50px' }}>
                    <CircularProgress />
                </TableCell>
            </TableRow>
        );
    } else if (scheduleData?.length > 0) {
        scheduleTable = scheduleData.map((item, index) => (
            <TableRow key={item.id}>
                <TableCell>{(currentPage - 1) * itemsPerPage + index + 1}</TableCell>
                <TableCell>{item.schedule_date ? moment(item.schedule_date).format('DD-MM-YYYY') : ''}</TableCell>
                <TableCell>{item.assignee_name || ''}</TableCell>
                <TableCell>
                    <div style={{ display: 'inline-flex', alignItems: 'center', position: 'relative' }}>
                        <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: item.status === 'Completed' ? '#e8f5e9' : '#fff3e0',
                            color: item.status === 'Completed' ? '#2e7d32' : '#e65100'
                        }}>
                            {item.status}
                        </span>
                        {item.status === 'Scheduled' && (
                            <Can do="manage" on="AddCalibrationReport">
                                <Tooltip title="Change status">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleStatusChange(item.id, 'Completed')}
                                        style={{ color: '#e65100', padding: '2px', marginLeft: '2px' }}
                                    >
                                        <ArrowDropDownIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Can>
                        )}
                    </div>
                </TableCell>
            </TableRow>
        ));
    } else {
        scheduleTable = (
            <TableRow>
                <TableCell colSpan={scheduleColumnNames.length} align="center">
                    <h5 className='w3-text-grey'>No Data Found</h5>
                </TableCell>
            </TableRow>
        );
    }

    return (
        <Grid container sx={{ mt: 2 }}>
            {/* Add Schedule Section */}
            <Can do="manage" on="AddCalibrationReport">
                <Grid item xs={12} sx={{ mb: 2 }}>
                    <Button
                        style={{ backgroundColor: '#008080', color: 'white' }}
                        variant="contained"
                        onClick={() => {
                            if (showAddForm) {
                                // Clear fields when canceling
                                setScheduleRows([{ id: 1, schedule_date: null, assignee_name: '' }]);
                            }
                            setShowAddForm(!showAddForm);
                        }}
                    >
                        {showAddForm ? 'Cancel' : 'Add Schedule'}
                    </Button>
                </Grid>
            </Can>

            {showAddForm && (
                <Grid item xs={12} sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={4}>
                                <div style={{ fontSize: '14px' }}>
                                    <strong>Facility name:</strong> {facilityName || facilityId}
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <div style={{ fontSize: '14px' }}>
                                    <strong>Latest Calibration date:</strong>{' '}
                                    {latestCalibrationDate
                                        ? moment(latestCalibrationDate).format('DD:MM:YYYY')
                                        : 'N/A'}
                                </div>
                            </Grid>
                            <Grid item xs={12} sm={4} style={{ textAlign: 'right' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddRow}
                                    style={{ borderColor: '#008080', color: '#008080' }}
                                >
                                    ADD
                                </Button>
                            </Grid>
                        </Grid>

                        {scheduleRows.map((row, index) => (
                            <Grid container spacing={2} key={row.id} alignItems="center" sx={{ mb: 1 }}>
                                <Grid item xs={12} sm={5}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label="Date of Schedule"
                                            value={row.schedule_date ? dayjs(row.schedule_date) : null}
                                            onChange={(newValue) => handleRowChange(row.id, 'schedule_date', newValue)}
                                            format="DD/MM/YYYY"
                                            slotProps={{
                                                textField: {
                                                    size: 'small',
                                                    fullWidth: true,
                                                    sx: inputLabelFocusedStyle
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <FormControl fullWidth size="small" sx={selectFocusedStyle}>
                                        <InputLabel sx={inputLabelFocusedStyle}>Assignee</InputLabel>
                                        <Select
                                            value={row.assignee_name}
                                            label="Assignee"
                                            onChange={(e) => handleRowChange(row.id, 'assignee_name', e.target.value)}
                                        >
                                            {greenUsers.map((user, idx) => (
                                                <MenuItem key={idx} value={user}>{user}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    {scheduleRows.length > 1 && (
                                        <IconButton onClick={() => handleRemoveRow(row.id)} size="small">
                                            <CloseIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        ))}

                        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving}
                                style={{ backgroundColor: '#008080', color: 'white' }}
                            >
                                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                            </Button>
                        </Grid>
                    </Paper>
                </Grid>
            )}

            {/* Schedule Table */}
            <Grid item xs={12}>
                <Grid container justifyContent="flex-end" sx={{ mb: 1 }}>
                    {scheduleData?.length > 0 && (
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleDownload}
                            style={{ backgroundColor: '#008080', color: 'white' }}
                        >
                            Download
                        </Button>
                    )}
                </Grid>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {scheduleColumnNames.map((col, idx) => (
                                    <TableCell key={idx} className='font-weight-600 font-size-14'>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scheduleTable}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {/* Pagination */}
            {(!showLoader && scheduleData?.length > 0) && (
                <Grid container item xs={12} style={{ marginBottom: '30px', marginTop: '12px' }}>
                    <Grid item xs={12} display="flex" justifyContent="end" style={{ marginBottom: '10px' }}>
                        <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                            {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}-
                            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                        </span>
                    </Grid>
                    <Grid item xs={12} display="flex" justifyContent="center">
                        <Pagination
                            className='pagination'
                            page={currentPage}
                            count={totalPages}
                            variant="outlined"
                            shape="rounded"
                            sx={paginationStyle}
                            onChange={handlePageChange}
                        />
                    </Grid>
                </Grid>
            )}

            {alertMessage && (
                <AlertDialog
                    handleClose={() => setAlertMessage(null)}
                    message={alertMessage}
                    type={msgType}
                    handleDialogClose={() => setAlertMessage(null)}
                />
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ open: false, scheduleId: null })}
            >
                <DialogTitle>Confirm Status Change</DialogTitle>
                <DialogContent>
                    Are you sure you want to mark this schedule as <strong>Completed</strong>?
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialog({ open: false, scheduleId: null })}
                        style={{ color: '#666' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmStatusUpdate}
                        variant="contained"
                        style={{ backgroundColor: '#008080', color: 'white' }}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
